import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, Brain, Boxes, Cpu, Factory, Hand, Eye, Sparkles, ShieldCheck, ArrowRight } from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import RoboticsConsultationForm from "@/components/RoboticsConsultationForm";
import puduD7Asset from "@/assets/pudu-d7.webp.asset.json";
const puduD7Img = puduD7Asset.url;
import puduD9Img from "@/assets/robots/pudu-d9.png";

const PuduD7 = () => {
  const [formOpen, setFormOpen] = useState(false);

  const valuePoints = [
    { icon: <Brain className="w-6 h-6" />, title: "PuduFM 1.0 Embodied Intelligence", desc: "Powered by the PuduFM 1.0 foundation model for real-world embodied AI reasoning and continuous skill learning." },
    { icon: <Factory className="w-6 h-6" />, title: "Built for Industrial Environments", desc: "Engineered for manufacturing floors, warehouses, intralogistics, and complex factory operations." },
    { icon: <Sparkles className="w-6 h-6" />, title: "Learns From Real Workflows", desc: "Improves over time by training on the workflows it operates in — adapting to your facility, not the other way around." },
    { icon: <Boxes className="w-6 h-6" />, title: "Warehouse & Material Handling", desc: "Shelf picking, inventory replenishment, material movement, and intralogistics across staging, sortation, and line-side delivery." },
    { icon: <Hand className="w-6 h-6" />, title: "Advanced Perception & Dexterity", desc: "Multi-modal perception with dexterous manipulation built for precision tasks in unstructured environments." },
    { icon: <Cpu className="w-6 h-6" />, title: "Future-Ready Automation", desc: "Ideal for operators exploring the next generation of intelligent industrial automation alongside their human teams." },
  ];

  return (
    <>
      <SEO
        title="PUDU D7 Industrial Semi-Humanoid Robot | Neil AI Global Solutions"
        description="PUDU D7 next-generation industrial semi-humanoid robot for manufacturing automation, warehouse handling, shelf picking, and intralogistics. Deployed by Neil AI — PUDU Robotics authorized dealer."
        keywords="PUDU D7, industrial humanoid robot, PUDU Robotics authorized dealer, warehouse automation robot, manufacturing automation robot, embodied AI robot, intralogistics robot, AI robotics company North Carolina"
        canonical="https://www.neilai.ai/robots/pudu-d7"
      />
      <Navigation />

      <main className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
        <div className="container mx-auto px-4 pt-32 md:pt-40 pb-12">
          <Button asChild variant="ghost" className="mb-6">
            <Link to="/robots"><ArrowLeft className="mr-2 w-4 h-4" />Back to Robots</Link>
          </Button>

          {/* Hero */}
          <section className="grid lg:grid-cols-2 gap-10 mb-20 items-center">
            <div className="relative bg-gradient-to-br from-white/5 to-primary/10 rounded-2xl p-8 flex items-center justify-center border border-[hsl(var(--line))] min-h-[420px]">
              <img
                src={puduD7Img}
                alt="PUDU D7 industrial semi-humanoid robot — Neil AI Global Solutions"
                className="w-full h-auto max-h-[560px] object-contain drop-shadow-[0_10px_40px_rgba(0,163,255,0.25)]"
                loading="eager"
              />
              <Badge className="absolute top-4 right-4 bg-primary text-primary-foreground font-bold px-3 py-1">
                PUDU Robotics Authorized Dealer
              </Badge>
            </div>

            <div className="space-y-6">
              <p className="text-primary font-semibold tracking-wider uppercase text-sm">Next-Generation Industrial Semi-Humanoid Robot</p>
              <h1 className="font-orbitron font-bold text-3xl md:text-5xl lg:text-6xl leading-tight">PUDU D7</h1>
              <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
                The PUDU D7 is Pudu Robotics' next-generation industrial semi-humanoid robot, built for embodied AI, manufacturing automation, warehouse workflows, intralogistics, material handling, shelf picking, inventory replenishment, and complex factory operations.
              </p>
              <div className="rounded-xl border border-[hsl(var(--line))] bg-white/5 p-5">
                <p className="text-sm text-muted-foreground leading-relaxed">
                  <span className="text-foreground font-semibold">Powered by Neil AI Global Solutions.</span> We help businesses evaluate, plan, and deploy advanced robotics solutions like the PUDU D7 to reduce repetitive work, improve operational efficiency, and prepare for the next generation of intelligent automation.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button size="lg" className="glow-gold flex-1" onClick={() => setFormOpen(true)}>Request D7 Information</Button>
                <Button asChild size="lg" variant="outline" className="flex-1">
                  <a href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics" target="_blank" rel="noopener noreferrer">Schedule a Robotics Consultation</a>
                </Button>
                <Button asChild size="lg" variant="outline" className="flex-1">
                  <Link to="/robots">See Robotics Solutions</Link>
                </Button>
              </div>
            </div>
          </section>

          {/* Value Points */}
          <section className="mb-20">
            <div className="mb-10 text-center max-w-3xl mx-auto">
              <h2 className="font-orbitron text-2xl md:text-4xl font-bold mb-3">Industrial Intelligence, Engineered for Real Work</h2>
              <p className="text-muted-foreground">Embodied AI built for the operators, integrators, and manufacturers shaping the next decade of automation.</p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {valuePoints.map((v, i) => (
                <Card key={i} className="glass-effect border-[hsl(var(--line))] hover:border-primary/40 transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="text-primary mb-3">{v.icon}</div>
                    <h3 className="font-bold text-lg mb-2">{v.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{v.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          {/* Comparison */}
          <ComparisonSection puduD7Img={puduD7Img} puduD9Img={puduD9Img} />

          {/* Lead capture */}
          <LeadCaptureCTA onOpen={() => setFormOpen(true)} />
        </div>
      </main>

      <Footer />
      <RoboticsConsultationForm open={formOpen} onOpenChange={setFormOpen} defaultRobot="PUDU D7" />
    </>
  );
};

export const ComparisonSection = ({ puduD7Img, puduD9Img }: { puduD7Img: string; puduD9Img: string }) => (
  <section className="mb-20">
    <div className="text-center max-w-3xl mx-auto mb-10">
      <h2 className="font-orbitron text-2xl md:text-4xl font-bold mb-3">D7 vs D9: Which Humanoid Robotics Solution Fits Your Business?</h2>
      <p className="text-muted-foreground">Two distinct platforms from PUDU Robotics — deployed and supported by Neil AI Global Solutions.</p>
    </div>
    <div className="grid md:grid-cols-2 gap-6">
      <Card className="glass-effect border-[hsl(var(--line))] hover:border-primary/40 transition-all">
        <CardContent className="p-8">
          <img src={puduD7Img} alt="PUDU D7" className="h-40 mx-auto object-contain mb-4" />
          <h3 className="font-orbitron text-2xl font-bold mb-2">PUDU D7</h3>
          <p className="text-primary text-sm font-semibold mb-3">Industrial Semi-Humanoid</p>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Best for industrial automation, warehouses, factories, intralogistics, shelf picking, material movement, and continuous workflow learning.
          </p>
        </CardContent>
      </Card>
      <Card className="glass-effect border-[hsl(var(--line))] hover:border-primary/40 transition-all">
        <CardContent className="p-8">
          <img src={puduD9Img} alt="PUDU D9" className="h-40 mx-auto object-contain mb-4" />
          <h3 className="font-orbitron text-2xl font-bold mb-2">PUDU D9</h3>
          <p className="text-primary text-sm font-semibold mb-3">Full-Sized Bipedal Humanoid</p>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Best for full-sized humanoid research, advanced mobility, service interaction, object handling, future commercial pilots, and human-like task support.
          </p>
        </CardContent>
      </Card>
    </div>
    <p className="text-xs text-muted-foreground/80 text-center mt-6 max-w-3xl mx-auto leading-relaxed">
      Availability, functionality, pricing, and deployment options may vary by configuration and manufacturer release schedule. Neil AI Global Solutions will help your organization evaluate the right robotics solution for your environment.
    </p>
  </section>
);

export const LeadCaptureCTA = ({ onOpen }: { onOpen: () => void }) => (
  <section className="rounded-2xl border border-[hsl(var(--line))] bg-gradient-to-br from-primary/10 via-background to-background p-8 md:p-12 text-center">
    <ShieldCheck className="w-10 h-10 text-primary mx-auto mb-4" />
    <h2 className="font-orbitron text-2xl md:text-4xl font-bold mb-3">Ready to Explore Humanoid Robotics for Your Business?</h2>
    <p className="text-muted-foreground max-w-2xl mx-auto mb-6">
      Neil AI Global Solutions helps businesses identify the right AI and robotics solutions for cleaning, service, logistics, manufacturing, hospitality, healthcare, and operational support.
    </p>
    <Button size="lg" className="glow-gold" onClick={onOpen}>
      Request Robotics Consultation <ArrowRight className="ml-2 w-4 h-4" />
    </Button>
    <p className="text-xs text-muted-foreground/70 mt-4">PUDU Robotics Authorized Dealer · Powered by Neil AI Global Solutions</p>
  </section>
);

export default PuduD7;
